import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Receipt, Plus, DollarSign, AlertTriangle, Wallet } from "lucide-react";
import { toast } from "sonner";
import RecordPaymentDialog, { InvoiceStatusPill, INVOICE_STATUS_META } from "@/components/RecordPaymentDialog";
import Payments from "@/pages/Payments";
import CustomerCommsButtons from "@/components/CustomerCommsButtons";

const money = (v) => `$${(Number(v || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Quick-filter definitions: each returns true if the row matches
const QUICK_FILTERS = [
  { key: "all",      label: "All",           match: () => true },
  { key: "unpaid",   label: "Unpaid",        match: (i) => ["sent", "viewed", "partial", "overdue"].includes(effectiveStatus(i)) },
  { key: "overdue",  label: "Overdue",       match: (i) => effectiveStatus(i) === "overdue" },
  { key: "partial",  label: "Partial",       match: (i) => effectiveStatus(i) === "partial" },
  { key: "paid",     label: "Paid",          match: (i) => effectiveStatus(i) === "paid" },
  { key: "draft",    label: "Draft",         match: (i) => i.status === "draft" },
  { key: "void",     label: "Void / Cancel", match: (i) => ["void", "cancelled"].includes(i.status) },
];

function effectiveStatus(inv) {
  return inv.computed_status || inv.status || "draft";
}

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("unpaid");
  const [selectJobOpen, setSelectJobOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [tab, setTab] = useState("invoices"); // invoices | payments
  const nav = useNavigate();

  const load = async () => {
    const [{ data: inv }, { data: js }] = await Promise.all([api.get("/invoices"), api.get("/jobs")]);
    setItems(inv);
    setJobs(js);
  };
  useEffect(() => { load(); }, []);

  const createFromJob = async (jobId) => {
    try {
      const { data } = await api.post(`/invoices/from-job/${jobId}`);
      toast.success(`Created ${data.invoice_number}`);
      setSelectJobOpen(false);
      nav(`/app/invoices/${data.id}`);
    } catch { toast.error("Failed"); }
  };

  const activeFilter = QUICK_FILTERS.find((f) => f.key === filter) || QUICK_FILTERS[0];
  const filtered = items.filter((i) => {
    if (!activeFilter.match(i)) return false;
    if (!q) return true;
    const t = q.toLowerCase();
    return (i.invoice_number || "").toLowerCase().includes(t) ||
           (i.customer_name || "").toLowerCase().includes(t) ||
           (i.title || "").toLowerCase().includes(t);
  });

  // Counts for each filter
  const counts = QUICK_FILTERS.reduce((acc, f) => {
    acc[f.key] = items.filter((i) => f.match(i)).length;
    return acc;
  }, {});

  const totals = items.reduce((acc, i) => {
    const s = effectiveStatus(i);
    acc.total += (i.total || 0);
    if (s === "paid") acc.paid += (i.total || 0);
    else if (["sent", "viewed", "partial", "overdue"].includes(s)) acc.outstanding += (i.balance != null ? i.balance : (i.total || 0));
    if (s === "overdue") acc.overdue += (i.balance != null ? i.balance : (i.total || 0));
    return acc;
  }, { total: 0, paid: 0, outstanding: 0, overdue: 0 });

  const openPay = (e, inv) => { e.stopPropagation(); setPayTarget(inv); };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Billing</div>
          <h1 className="font-heading text-4xl font-bold">Invoices</h1>
        </div>
        <Button onClick={() => setSelectJobOpen(true)} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="invoices-new-btn">
          <Plus className="w-4 h-4 mr-1"/> Invoice from job
        </Button>
      </div>

      {/* Iter 2026-02 — Tabs: switch between the invoice list and the payments-by-type dashboard */}
      <div className="flex gap-1 border-b border-zinc-200 mb-6">
        <button
          onClick={() => setTab("invoices")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-2 ${tab === "invoices" ? "border-[#FF5A00] text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}
          data-testid="invoices-tab-invoices"
        ><Receipt className="w-4 h-4"/> Invoices</button>
        <button
          onClick={() => setTab("payments")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-2 ${tab === "payments" ? "border-[#FF5A00] text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}
          data-testid="invoices-tab-payments"
        ><Wallet className="w-4 h-4"/> Payments by type</button>
      </div>

      {tab === "payments" ? (
        <div className="-mx-8 -mt-2"><Payments/></div>
      ) : (
      <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat label="Total invoiced" value={money(totals.total)}/>
        <Stat label="Collected" value={money(totals.paid)} accent="emerald"/>
        <Stat label="Outstanding" value={money(totals.outstanding)} accent="amber"/>
        <Stat label="Overdue" value={money(totals.overdue)} accent="rose"/>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden">
        <div className="p-4 border-b border-zinc-100 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search number, customer, title" className="pl-9"/>
          </div>
          <div className="flex gap-1 flex-wrap">
            {QUICK_FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`text-xs px-3 py-1.5 rounded-md border capitalize inline-flex items-center gap-1.5 ${filter === f.key ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"}`}
                data-testid={`invoices-filter-${f.key}`}
              >
                {f.label}
                <span className={`px-1 rounded font-mono-accent text-[9px] ${filter === f.key ? "bg-white/20" : "bg-zinc-100"}`}>{counts[f.key] || 0}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Number</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-right px-4 py-3">Total</th>
                <th className="text-right px-4 py-3">Balance</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Due</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => {
                const es = effectiveStatus(i);
                const canPay = !["paid", "void", "cancelled", "draft"].includes(i.status) && Number(i.balance || 0) > 0.005;
                return (
                  <tr key={i.id} onClick={() => nav(`/app/invoices/${i.id}`)} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer" data-testid={`invoice-row-${i.id}`}>
                    <td className="px-4 py-3 font-mono-accent text-zinc-700">{i.invoice_number}</td>
                    <td className="px-4 py-3 font-medium">
                      <div>{i.customer_name}</div>
                      {i.customer_id && (
                        <div className="mt-1">
                          <CustomerCommsButtons
                            customer={{
                              id: i.customer_id,
                              phone: i.customer_phone,
                              email: i.customer_email,
                              sms_opted_out: i.customer_sms_opted_out,
                            }}
                            invoiceId={i.id}
                            rowTestId={`invoice-row-${i.id}`}
                          />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono-accent text-right">{money(i.total)}</td>
                    <td className="px-4 py-3 font-mono-accent text-right">
                      <span className={Number(i.balance || 0) > 0 ? "text-zinc-900 font-semibold" : "text-zinc-400"}>{money(i.balance)}</span>
                    </td>
                    <td className="px-4 py-3"><InvoiceStatusPill status={i.status} computed={i.computed_status}/></td>
                    <td className="px-4 py-3 text-zinc-500 text-xs font-mono-accent">
                      {i.due_date ? new Date(i.due_date).toLocaleDateString() : "—"}
                      {es === "overdue" && <AlertTriangle className="inline w-3 h-3 ml-1 text-rose-500"/>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canPay && (
                        <button
                          onClick={(e) => openPay(e, i)}
                          className="text-xs px-2.5 py-1 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 inline-flex items-center gap-1 font-medium"
                          data-testid={`invoice-quick-pay-${i.id}`}
                          title="Record payment"
                        >
                          <DollarSign className="w-3 h-3"/> Mark Paid
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan="7" className="text-center text-zinc-500 py-12">
                  <Receipt className="w-8 h-8 text-zinc-300 mx-auto mb-2"/>
                  No invoices match the current filter.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectJobOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectJobOpen(false)}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-xl font-bold mb-4">Pick a job to invoice</h3>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {jobs.map((j) => (
                <button key={j.id} onClick={() => createFromJob(j.id)} className="w-full text-left border border-zinc-200 rounded-lg p-3 hover:border-[#FF5A00] hover:bg-orange-50" data-testid={`select-job-${j.id}`}>
                  <div className="font-medium">{j.title}</div>
                  <div className="text-xs text-zinc-500">{j.customer_name} · ${(j.value || 0).toLocaleString()}</div>
                </button>
              ))}
              {jobs.length === 0 && <div className="text-center text-zinc-500 py-8">No jobs yet.</div>}
            </div>
          </div>
        </div>
      )}

      {payTarget && (
        <RecordPaymentDialog
          invoice={payTarget}
          open={!!payTarget}
          onOpenChange={(v) => { if (!v) setPayTarget(null); }}
          onSaved={() => { setPayTarget(null); load(); }}
        />
      )}
      </>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  const cls = accent === "emerald" ? "text-emerald-700" : accent === "amber" ? "text-amber-700" : accent === "rose" ? "text-rose-700" : "text-zinc-900";
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5 card-crisp">
      <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">{label}</div>
      <div className={`font-heading text-3xl font-bold mt-1 ${cls}`}>{value}</div>
    </div>
  );
}
